// Höhe und Zugang am Dach in der HE-Doku (E3c, S5; ANFORDERUNGEN.md H32: „wie
// hoch das dort ist, ob er wegen der Leiter …“) – was die Station „Dach“
// rechnet, ohne React: die Höhe lesen, wie man sie tippt („6“, „6,5“, „ca.
// 7“), und so zeigen; aus den drei Feldern die Angabe nach dem Vertrag im Kern
// (dach-fakten.ts). Was nicht passt, wird nicht gespeichert – die Seite sagt,
// was sie braucht. Freiwillig, nichts muss.
//
// Gespeichert und gesendet wird in arbeitskopie.ts (`dachFaktenSpeichern`),
// wie „Sonstiges“ – auch im Nachtrag.

import { DACH_GRENZEN, dachFaktenVertrag, type DachFakten, type DachZugang } from "@happy-energy/kern";

/**
 * Die Höhe aus dem Feld: leer → null (keine Angabe); „6“, „6,5“, „6.5“, „ca.
 * 7“, „7 m“ → die Zahl; sonst undefined (keine brauchbare Höhe). Die Grenzen
 * (0 bis 30, eine Kommastelle) prüft der Vertrag in `dachAngabe`.
 */
export function hoeheLesen(eingabe: string): number | null | undefined {
  const t = eingabe
    .trim()
    .toLowerCase()
    .replace(/^ca\.?\s*/, "")
    .replace(/\s*m$/, "")
    .replace(",", ".");
  if (t === "") return null;
  if (!/^\d{1,2}(\.\d*)?$/.test(t)) return undefined;
  const zahl = Number(t);
  return Number.isFinite(zahl) ? zahl : undefined;
}

/** Die Höhe fürs Feld: 6 → „6“, 6.5 → „6,5“; ohne → „“. */
export function hoeheZeigen(m: number | null | undefined): string {
  return m === null || m === undefined ? "" : String(Math.round(m * 10) / 10).replace(".", ",");
}

/** Was in den Feldern steht – so, wie getippt und angetippt. */
export interface DachEingabe {
  hoehe: string;
  zugang: DachZugang | null;
  hinweis: string;
}

/** Die Felder, wie sie aus einer gespeicherten Angabe beginnen. */
export function dachEingabeAus(f: DachFakten | null | undefined): DachEingabe {
  return { hoehe: hoeheZeigen(f?.hoehe_m), zugang: f?.zugang ?? null, hinweis: f?.hinweis ?? "" };
}

/** Die Meldung, wenn die Höhe nicht passt. */
export const HOEHE_MELDUNG = `Bitte die Höhe in Metern, z. B. 6,5 – höchstens ${DACH_GRENZEN.hoeheMax} m, eine Kommastelle.`;

/**
 * Aus den Feldern die Angabe nach dem Vertrag – oder die Meldung, was nicht
 * passt. Leere Felder fallen weg; ganz leer ist `{}` („nichts angegeben“).
 */
export function dachAngabe(e: DachEingabe): { ok: true; fakten: DachFakten } | { ok: false; meldung: string } {
  const hoehe = hoeheLesen(e.hoehe);
  if (hoehe === undefined) return { ok: false, meldung: HOEHE_MELDUNG };
  const gelesen = dachFaktenVertrag.safeParse({
    ...(hoehe !== null ? { hoehe_m: hoehe } : {}),
    ...(e.zugang !== null ? { zugang: e.zugang } : {}),
    ...(e.hinweis.trim() !== "" ? { hinweis: e.hinweis } : {}),
  });
  if (!gelesen.success) {
    const stelle = gelesen.error.issues[0]?.path[0];
    return { ok: false, meldung: stelle === "hoehe_m" ? HOEHE_MELDUNG : (gelesen.error.issues[0]?.message ?? "Bitte prüfen.") };
  }
  return { ok: true, fakten: gelesen.data };
}
